import { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import userService from '../services/userService'
import { isOwnerOrCompany } from '../utils/permissions'
import '../styles/deposit-modal.css'
import '../styles/change-status-modal.css'

// Same inline styles the reference's toggle (vue-js-toggle-button, 50×22) writes on its
// nodes — they are what beats the ported `.vue-js-switch .v-switch-core { 60px × 24px }` rule.
const switchCoreStyle = (on) => ({
  width: '50px',
  height: '22px',
  backgroundColor: on ? 'rgb(0, 136, 204)' : 'rgb(191, 203, 217)',
  borderRadius: '11px'
})
const switchButtonStyle = (on) => ({
  width: '16px',
  height: '16px',
  transition: 'transform 300ms',
  transform: `translate3d(${on ? 31 : 3}px, 3px, 0px)`
})

/**
 * ChangeStatusModal Component
 * 
 * Modal component for changing user status (User Active and Bet Active).
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Function} props.onHide - Function to close the modal
 * @param {Object} props.userData - User data (optional)
 * @param {Function} props.onSubmit - Callback function when form is submitted
 */
function ChangeStatusModal({ show, onHide, userData = null, onSubmit }) {
  const [formData, setFormData] = useState({
    userActive: false,
    betActive: false,
    transactionPassword: ''
  })
  const [wasValidated, setWasValidated] = useState(false)
  const [passwordError, setPasswordError] = useState('')

  // ── JS Game Lock (one toggle) ──
  // ON = ALL JS games (live casino, slots, crash, fantasy) locked for this account and
  // its whole downline — server row game_uid '*'. OFF = no lock rows (also clears old
  // per-game locks). initialLockUids: server state when the popup opened.
  const ALL_GAMES = '*'
  const [gameLocked, setGameLocked] = useState(false)
  const [lockedByUpline, setLockedByUpline] = useState(false)
  const [initialLockUids, setInitialLockUids] = useState([])
  const [locksLoading, setLocksLoading] = useState(false)
  const [locksError, setLocksError] = useState('')

  // Game Lock sirf Owner / Company panel ko (original jaisa); baaki panels ko
  // popup me sirf User Active, Bet Active aur Transaction Password.
  const canGameLock = isOwnerOrCompany()

  const accountType = userData?.user_type === 'USER' ? 'USER' : 'STAFF'
  const accountId = userData ? (userData.user_type === 'USER' ? userData.user_id : userData.staff_id) : null

  // Load the account's lock each time the popup opens.
  useEffect(() => {
    if (!show || !accountId || !canGameLock) return
    let cancelled = false
    setLocksError('')
    setLocksLoading(true)
    userService.getJsGameLocks(accountType, accountId)
      .then((res) => {
        if (cancelled) return
        const uids = res?.success && Array.isArray(res.data) ? res.data.map((g) => g.game_uid) : []
        setGameLocked(uids.includes(ALL_GAMES))
        setLockedByUpline(!!res?.lockedByUpline)
        setInitialLockUids(uids)
      })
      .catch((err) => {
        if (cancelled) return
        setGameLocked(false)
        setLockedByUpline(false)
        setInitialLockUids([])
        setLocksError(err?.response?.data?.error || 'Could not load game lock')
      })
      .finally(() => { if (!cancelled) setLocksLoading(false) })
    return () => { cancelled = true }
  }, [show, accountType, accountId, canGameLock])

  const wantedLockUids = gameLocked ? [ALL_GAMES] : []
  const locksChanged = () => wantedLockUids.join('|') !== [...initialLockUids].sort().join('|')

  // Populate form from userData if provided
  useEffect(() => {
    if (show && userData) {
      // Map status field: "Active" -> true, "InActive" -> false
      const userActive = userData.status === 'Active' ||
        (userData.userActive !== undefined ? userData.userActive :
          (userData.active !== undefined ? userData.active : false))

      setFormData(prev => ({
        ...prev,
        userActive: userActive,
        betActive: userData.bet_locked !== undefined ? !userData.bet_locked : (userData.betActive !== undefined ? userData.betActive : true),
        transactionPassword: ''
      }))
    }
  }, [show, userData])

  const handleToggle = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }))
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear password error when user types
    if (name === 'transactionPassword' && passwordError) {
      setPasswordError('')
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const form = e.currentTarget

    // First check basic form validity
    if (form.checkValidity() === false) {
      e.stopPropagation()
      setWasValidated(true)
      return
    }

    // Validate transaction password - must be "12345"
    // const correctPassword = '12345'
    // if (formData.transactionPassword !== correctPassword) {
    //   setPasswordError('Invalid transaction password. Please enter the correct password.')
    //   setWasValidated(true)
    //   e.stopPropagation()
    //   return
    // }

    // Clear any previous errors
    setPasswordError('')
    setWasValidated(false)

    // Proceed with submission
    if (onSubmit) {
      onSubmit({
        ...formData,
        jsGameLocks: wantedLockUids,
        jsGameLocksChanged: canGameLock && !locksError && locksChanged()
      })
    }
    // Reset form
    const userActive = userData?.status === 'Active' ||
      (userData?.userActive !== undefined ? userData.userActive : false)
    setFormData({
      userActive: userActive,
      betActive: userData?.bet_locked !== undefined ? !userData.bet_locked : (userData?.betActive !== undefined ? userData.betActive : true),
      transactionPassword: ''
    })
    onHide()
  }

  const handleClose = () => {
    // Reset form on close
    const userActive = userData?.status === 'Active' ||
      (userData?.userActive !== undefined ? userData.userActive : false)
    setFormData({
      userActive: userActive,
      betActive: userData?.bet_locked !== undefined ? !userData.bet_locked : (userData?.betActive !== undefined ? userData.betActive : true),
      transactionPassword: ''
    })
    setWasValidated(false)
    setPasswordError('')
    onHide()
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="modal-md modal-dialog-scrollable"
    >
      {/* reference renders <header class="modal-header"> */}
      <header className="modal-header">
        <Modal.Title as="h4">Change Status</Modal.Title>
        <button
          type="button"
          className="close text-white"
          onClick={handleClose}
          aria-label="Close"
        >
          ×
        </button>
      </header>
      <Modal.Body>
        {/* reference: the username sits ABOVE the form, in .m-t-20 > .row > .col-6.text-left */}
        <div className="m-t-20">
          <div className="row">
            <div className="col-6 text-left">
              <p id="status-username" className="text-warning">
                {userData?.username || userData?.userName || ''}
              </p>
            </div>
          </div>
        </div>
        <form
          onSubmit={handleSubmit}
          data-vv-scope="changeStatusMDL"
          method="post"
          className={wasValidated ? 'was-validated' : ''}
          noValidate
        >
          {/* Toggle Switches */}
          <div className="form-group row">
            <div className="col-6 text-center">
              <label className="col-form-label d-block">User Active</label>
              <label
                className={`vue-js-switch ${formData.userActive ? 'toggled' : ''}`}
                onClick={() => handleToggle('userActive')}
              >
                <input
                  type="checkbox"
                  className="v-switch-input"
                  checked={formData.userActive}
                  onChange={() => handleToggle('userActive')}
                />
                <div className="v-switch-core" style={switchCoreStyle(formData.userActive)}>
                  <div className="v-switch-button" style={switchButtonStyle(formData.userActive)}></div>
                </div>
                <span className={`v-switch-label ${formData.userActive ? 'v-left' : 'v-right'}`} style={{ lineHeight: '22px' }}>
                  {formData.userActive ? 'on' : 'off'}
                </span>
              </label>
            </div>
            <div className="col-6 text-center">
              <label className="col-form-label d-block">Bet Active</label>
              <label
                className={`vue-js-switch ${formData.betActive ? 'toggled' : ''}`}
                onClick={() => handleToggle('betActive')}
              >
                <input
                  type="checkbox"
                  className="v-switch-input"
                  checked={formData.betActive}
                  onChange={() => handleToggle('betActive')}
                />
                <div className="v-switch-core" style={switchCoreStyle(formData.betActive)}>
                  <div className="v-switch-button" style={switchButtonStyle(formData.betActive)}></div>
                </div>
                <span className={`v-switch-label ${formData.betActive ? 'v-left' : 'v-right'}`} style={{ lineHeight: '22px' }}>
                  {formData.betActive ? 'on' : 'off'}
                </span>
              </label>
            </div>
          </div>

          {/* Game Lock — ek toggle: ON = saare JS games (live casino, slots, crash,
              fantasy) is account aur uski poori downline ke liye lock.
              Sirf Owner / Company panel ko dikhta hai. */}
          {canGameLock && (
          <div className="form-group row js-game-lock">
            <div className="col-6 text-center">
              <label className="col-form-label d-block">Game Lock</label>
              {locksLoading ? (
                <div className="js-game-lock-note">Loading...</div>
              ) : locksError ? (
                <div className="js-game-lock-note text-danger">{locksError}</div>
              ) : (
                <label
                  className={`vue-js-switch ${gameLocked ? 'toggled' : ''}`}
                  onClick={() => setGameLocked((v) => !v)}
                >
                  <input
                    type="checkbox"
                    className="v-switch-input"
                    checked={gameLocked}
                    onChange={() => setGameLocked((v) => !v)}
                  />
                  <div className="v-switch-core" style={switchCoreStyle(gameLocked)}>
                    <div className="v-switch-button" style={switchButtonStyle(gameLocked)}></div>
                  </div>
                  <span className={`v-switch-label ${gameLocked ? 'v-left' : 'v-right'}`} style={{ lineHeight: '22px' }}>
                    {gameLocked ? 'on' : 'off'}
                  </span>
                </label>
              )}
            </div>
            {!locksLoading && !locksError && lockedByUpline && (
              <div className="col-6 js-game-lock-note text-danger">Games already locked by upline</div>
            )}
          </div>
          )}

          {/* Transaction Password */}
          <div className="form-group row">
            <label className="col-form-label col-4">Transaction Password</label>
            <div className="col-8 form-group-feedback-right pl-0">
              <input
                name="transactionPassword"
                type="password"
                className={`form-control ${passwordError ? 'is-invalid' : ''}`}
                value={formData.transactionPassword}
                onChange={handleChange}
                aria-required="true"
                required
              />
              {passwordError && (
                <div className="invalid-feedback d-block">
                  {passwordError}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-group row">
            <div className="col-12 text-right">
              <button
                type="button"
                className="btn btn-back"
                onClick={handleClose}
              >
                <i className="fas fa-undo mr-1"></i>
                Back
              </button>
              <button
                type="submit"
                className="btn btn-primary"
              >
                submit
                <i className="fas fa-sign-in-alt ml-1"></i>
              </button>
            </div>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  )
}

export default ChangeStatusModal

